import {
  AvatarColor,
  Badge,
  BadgeLevel,
  BadgeShape,
  BadgeSize,
  Button,
  ButtonLevel,
  Menu,
  MenuItem,
  MenuPopover,
  MenuTrigger,
  Separator,
  Text,
  TextColor,
  TextSize,
  TextWeight,
  avatarColorMap,
} from '@vizit/atoms';
import { cva, cx } from '@vizit/theme';
import * as React from 'react';
import type { Key } from 'react-aria-components';
import type { VerticalNavbarMenuItem } from './types';

const styles = {
  avatar: cva({ base: 'relative flex items-center justify-center text-white' }),
  avatarInner: cva({ base: 'absolute' }),
  avatarSize: cva({ base: 'h-18 w-18 text-sm max-sm:h-24 max-sm:w-24 max-sm:text-lg' }),
  menuHeader: cva({ base: 'flex items-start gap-4 p-6' }),
  menuUserInfo: cva({ base: 'flex flex-col gap-5 py-3 items-center justify-center' }),
  menuItemContent: cva({ base: 'flex items-center gap-4' }),
};

interface VerticalNavbarUserMenuProps {
  menuItems?: VerticalNavbarMenuItem[];
  userEmail?: string;
  avatarColor?: number;
  appVersion?: string;
  className?: string;
}

function SquareAvatar({
  children,
  color = AvatarColor.Gray,
  className,
}: {
  children: React.ReactNode;
  color?: AvatarColor;
  className?: string;
}) {
  return (
    <div className={cx(styles.avatar(), className)} style={{ backgroundColor: avatarColorMap[color] }}>
      <div className={styles.avatarInner()}>{children}</div>
    </div>
  );
}

function VerticalNavbarUserMenu(props: VerticalNavbarUserMenuProps) {
  const { menuItems, userEmail, avatarColor, appVersion, className } = props;

  const initials = React.useMemo(
    () =>
      (userEmail || 'Vizit')
        .split(' ')
        .map((name) => name[0])
        .join('')
        .toUpperCase(),
    [userEmail],
  );

  const handleMenuAction = (key: Key) => {
    const item = menuItems?.find((item) => item.key === key);
    if (item) {
      item.onAction();
    }
  };

  const color = (avatarColor ?? AvatarColor.Gray) as AvatarColor;

  return (
    <MenuTrigger>
      <Button
        aria-label="Open user navigation menu"
        style={{ padding: 0 }}
        level={ButtonLevel.Ghost}
        className={className}
        data-testid="user-menu-button"
        iconStart={
          <SquareAvatar color={color} className={styles.avatarSize()}>
            {initials}
          </SquareAvatar>
        }
      />
      <MenuPopover placement="right bottom" data-testid="user-menu">
        {userEmail && (
          <>
            <div className={styles.menuHeader()}>
              <SquareAvatar color={color} className={styles.avatarSize()}>
                {initials}
              </SquareAvatar>
              <div className={styles.menuUserInfo()}>
                <Text size={TextSize.Sm} color={TextColor.Tertiary}>
                  {userEmail}
                </Text>
              </div>
            </div>
            <Separator />
          </>
        )}
        {appVersion && (
          <>
            <div className="flex items-center gap-5 px-10 py-2" title={appVersion}>
              <Text size={TextSize.Xs} color={TextColor.Tertiary} weight={TextWeight.Regular}>
                current version:
              </Text>
              <Badge level={BadgeLevel.Default} shape={BadgeShape.Box} size={BadgeSize.Sm}>
                {appVersion.length > 24 ? `${appVersion.slice(0, 23)}…` : appVersion}
              </Badge>
            </div>
            <Separator />
          </>
        )}
        <Menu onAction={handleMenuAction}>
          {menuItems?.map((item) => (
            <MenuItem className="text-sm" id={item.key} key={item.key} data-testid={`user-menu-${item.key}`}>
              <div className={styles.menuItemContent()}>
                {item.icon}
                <Text size={TextSize.Sm} color={TextColor.Secondary} weight={TextWeight.Semibold}>
                  {item.label}
                </Text>
              </div>
            </MenuItem>
          ))}
        </Menu>
      </MenuPopover>
    </MenuTrigger>
  );
}

export { VerticalNavbarUserMenu };
export type { VerticalNavbarUserMenuProps };
